import React, { useState, useCallback, useEffect, useMemo } from 'react';

// NOTE:
// styleはlaunchと共通
import styles from '@/interface/ui/launch/index.module.scss';

import Items from '@/interface/ui/_shared/items/index';
import Menu from '@/interface/ui/_shared/items/menu';
import Layout from '@/interface/ui/_shared/layout/index';
import Spinner from '@/interface/ui/_shared/util/spinner';
import Head from 'next/head';
import { useInView } from 'react-intersection-observer';
import { useDispatch, useSelector } from 'react-redux';
import { useCachedScrollPosition } from '@/hooks/useCachedScrollPosition';

// types
import type { RootState } from '@/store/index';
import type { NextPage } from 'next';
import { useArchive } from '@/hooks/useArchive';
import { useShortcuts } from '@/hooks/useShortcuts';
import Shortcut from '@/domain/shortcuts';
import { ShortcutsBar } from '@/interface/ui/_shared/common/ShortcutsBar';
import { ShortcutsResultTitle } from '@/interface/ui/_shared/common/ShortcutsResultTitle';
import { useArchivedEvents } from '@/hooks/useArchivedEvents';
import ListsEvent from '@/domain/events';
import dayjs from 'dayjs';
import { Dispatch } from 'redux';
import type { ResolvedData as ResolvedPagingData } from '@/app/archives/archivesGetByPaging';
import archivesGetByPaging from '@/app/archives/archivesGetByPaging';
import { archivesLoadSucceeded } from '@/store/ducks/domain/archives';

interface Props {
  mode?: 'default';
  // paging: (scrollId: string, dispatch: Dispatch) => Promise<void>;
}

const meta = {
  title: 'atmos LISTs | アーカイブ',
  description: 'atmos LISTs | アーカイブ',
};

async function paging(scrollId: string, dispatch: Dispatch) {
  const data = await archivesGetByPaging({
    scrollId,
  });
  dispatch(archivesLoadSucceeded(data));
}

const Archives: NextPage<Props> = ({ mode }) => {
  useCachedScrollPosition();

  const { groupedArchives, fetchArchiveAsync, importArchiveData } =
    useArchive();

  const { archivedEvents, groupedArchivedEventData } = useArchivedEvents();
  const [eventShortcutSelected, setEventShortcutSelected] = useState(false);

  const {
    fetchItemsAsync: fetchShortcutItemsAsync,
    shortcuts,
    activeShortcut,
    clearActiveShortcut,
    itemCount,
    eventIds,
  } = useShortcuts('archive');

  const [isLoading, setLoading] = useState(false);
  const [rowNumber, setRowNumber] = useState(1);

  const dispatch = useDispatch();
  const scrollId = useSelector((state: RootState) => {
    if (state.domain.archives) {
      return state.domain.archives.scrollId;
    } else {
      return null;
    }
  });

  const { userAgent } = useSelector((state: RootState) => state.app);
  const hasNoMoreData = useSelector((state: RootState) => {
    if (state.domain.archives) return state.domain.archives.hasNoMoreData;
    return null;
  });

  const { ref, inView } = useInView({
    threshold: 0,
  });
  const loadMore = useCallback(async () => {
    if (!scrollId || hasNoMoreData || !inView) return;

    try {
      setLoading(true);
      await paging(scrollId, dispatch);
      setLoading(false);
    } catch (error) {
      setLoading(false);
    }
  }, [scrollId, hasNoMoreData, inView, dispatch]);

  useEffect(() => {
    if (inView) {
      void loadMore();
    }
  }, [inView, loadMore]);

  // note:
  // ショートカットが選択されており、その中にイベントidが入っている場合はイベントのみを表示する(商品は表示されない)
  const hasEventFilter = eventIds.length > 0;
  const filteredEventData = useMemo(() => {
    if (archivedEvents === undefined) return [];
    if (eventIds.length === 0) return [];

    const filteredEvents = archivedEvents?.filter((e: ListsEvent) =>
      eventIds.includes(e.id)
    );

    const grouped = {};

    if (filteredEvents) {
      for (const eventItem of filteredEvents) {
        const key = dayjs(eventItem.event_at).format('YYYYMMDD');
        const byReleaseAt: any[] = grouped[key] ? grouped[key] : [];
        byReleaseAt.push(eventItem);
        grouped[key] = byReleaseAt;
      }
    }

    return grouped;
  }, [archivedEvents, eventIds]);

  const renderArchiveItem = () => {
    const groupedData = eventShortcutSelected
      ? groupedArchivedEventData
      : hasEventFilter
        ? filteredEventData
        : groupedArchives;

    return (
      <ul className={styles.wrapper}>
        {Object.keys(groupedData).map((groupingKey, index) => {
          const groupingValue: Array<{ releaseAt: string }> =
            groupedData[groupingKey];

          const releaseAt = new Date(groupingValue[0].releaseAt);

          return (
            <li key={index} className={styles.itemWrapper}>
              <div className={styles.itemHeader}>
                <span className={styles.headerValue}>
                  {releaseAt.getMonth() + 1}
                </span>
                月
                <span className={styles.headerValue}>
                  {releaseAt.getDate()}
                </span>
                日<div></div>
              </div>
              <Items
                items={groupedData[groupingKey]}
                rowNumber={rowNumber}
                userAgent={userAgent}
                mode={mode}
              />
            </li>
          );
        })}
      </ul>
    );
  };

  const fetchShortcutItems = useCallback(
    async (shortcut: Shortcut) => {
      const data = await fetchShortcutItemsAsync(shortcut);

      if (data) {
        importArchiveData({ ...data, usingShortcuts: true }, true);
        setEventShortcutSelected(false);
      }
    },
    [fetchShortcutItemsAsync, importArchiveData]
  );

  const clearShortcutFilter = useCallback(async () => {
    await fetchArchiveAsync();
    clearActiveShortcut();
    setEventShortcutSelected(false);
  }, [clearActiveShortcut, fetchArchiveAsync]);

  const onPressShortcut = useCallback(
    async (shortcut: Shortcut) => {
      if (activeShortcut?.id === shortcut.id) {
        void clearShortcutFilter();
        return;
      }

      await fetchShortcutItems(shortcut);
    },
    [activeShortcut?.id, clearShortcutFilter, fetchShortcutItems]
  );

  const onPressEventShortcut = useCallback(() => {
    clearActiveShortcut();
    setEventShortcutSelected(true);
  }, [clearActiveShortcut]);

  if (!groupedArchives) return <Layout></Layout>;

  return (
    <>
      <Head>
        <title>{meta.title}</title>
        <meta name="description" content={meta.description} />
        <meta property="og:title" content={meta.title} key="title" />
      </Head>
      <Layout mode={mode}>
        <div className={styles.container}>
          <Menu
            rowNumber={rowNumber}
            setRowNumber={setRowNumber}
            props={{ mode }}
          />

          {shortcuts && (
            <ShortcutsBar
              shortcuts={shortcuts}
              onPress={onPressShortcut}
              onPressAll={clearShortcutFilter}
              onPressEvent={onPressEventShortcut}
              selected={activeShortcut?.id}
              hasEvent={false}
              eventSelected={eventShortcutSelected}
            />
          )}
          {!!activeShortcut && !hasEventFilter && (
            <ShortcutsResultTitle count={itemCount} />
          )}

          {renderArchiveItem()}
        </div>
        <div ref={ref} className={styles.loadingWrapper}>
          {isLoading && <Spinner />}
          <h2 className={styles.dumbFooter} />
        </div>
      </Layout>
    </>
  );
};

export default Archives;
